/**
 * PDFs on the device (brief section 7.4): the thumbnail of a PDF is its
 * first page, drawn here when it is uploaded (never uploaded by hand), and
 * Show on screen draws every page. pdf.js loads only when needed.
 */

async function pdfjs() {
  const lib = await import('pdfjs-dist');
  lib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString();
  return lib;
}

export async function openPdf(data: ArrayBuffer) {
  const lib = await pdfjs();
  return lib.getDocument({ data: new Uint8Array(data) }).promise;
}

export type PdfDocument = Awaited<ReturnType<typeof openPdf>>;

/** Draws one page at this width (CSS pixels) into a canvas. */
export async function drawPage(
  doc: PdfDocument,
  n: number,
  width: number,
  canvas: HTMLCanvasElement,
): Promise<void> {
  const page = await doc.getPage(n);
  const base = page.getViewport({ scale: 1 });
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const viewport = page.getViewport({ scale: (width / base.width) * ratio });
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  canvas.style.width = `${Math.round(viewport.width / ratio)}px`;
  canvas.style.height = `${Math.round(viewport.height / ratio)}px`;
  await page.render({ canvas, canvasContext: canvas.getContext('2d')!, viewport }).promise;
}

/** The first page as a JPEG of 960 pixels wide; null when the PDF cannot be drawn. */
export async function firstPageThumbnail(file: Blob): Promise<Blob | null> {
  try {
    const doc = await openPdf(await file.arrayBuffer());
    const canvas = document.createElement('canvas');
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: 960 / base.width });
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const context = canvas.getContext('2d')!;
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvas, canvasContext: context, viewport }).promise;
    await doc.loadingTask.destroy();
    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8));
  } catch {
    return null;
  }
}
